import { storefront } from '../client';
import { logError } from '../../utilities/logging';
import { chunk } from '../../utilities/chunk';

/**
 * @typedef {Object} ProductVariant
 * @property {string} id
 * @property {string} title
 * @property {string} handle
 * @property {Object} price
 * @property {string} price.amount
 * @property {string} price.currencyCode
 * @property {Object} image
 * @property {string} image.url
 * @property {string} image.altText
 * @property {Object} product
 * @property {string} product.id
 * @property {string} product.title
 * @property {string} product.handle
 * @property {string[]} product.variants
 * @property {Object} asciiSymbol
 * @property {string} asciiSymbol.value
 * @property {boolean} availableForSale
 */

const getProductVariantsByIdQuery = `
query getProductVariantsByIdQuery($ids: [ID!]!, $country: CountryCode)
@inContext(country: $country) {
  nodes(ids: $ids) {
    ... on ProductVariant {
      id
      title
      price {
        amount
        currencyCode
      }
      compareAtPrice {
        amount
        currencyCode
      }
      image {
        url
        altText
      }
      product {
        id
        title
        handle,
        featuredImage {
          url
          altText
        }
        variantsCount {
          count
        }
      }
      asciiSymbol: metafield(namespace: "$app:component", key: "asciiSymbol") {
        value
      }
      hideFromVisualiser: metafield(namespace: "$app:personalisation", key: "hideFromVisualiser") {
        value
      }
      shortDescription: metafield(namespace: "$app:personalisation", key: "shortDescription") {
        value
      }
      availableForSale
    }
  }
}`;



/**
 * @param {Object} params
 * @param {string[]} params.ids
 * @param {string} params.country
 * @returns {Promise<ProductVariant[]>}
 */
export const fetchProductVariantsById = async ({ ids, country }) => {

  // Batch the requests into chunks of 250 in a functional style using Promise.all.
  const batches = chunk({ items: ids, size: 250 });
  const results = await Promise.all(batches.map(async (batch) => {
    const { data, errors } = await storefront.request(getProductVariantsByIdQuery, {
      variables: { ids: batch, country }
    });

    if (errors) {
      logError({
        label: 'fetchProductVariantsById',
        error: new Error('GraphQL errors occurred'),
        message: 'Error fetching product variants by ID',
        extraData: { ids: batch, country, errors },
        throwError: true
      });
    }

    return data.nodes;
  }));

  return results.flat();
};
